<script setup lang="ts" generic="T extends string | number">
import NxRadioItem from './NxRadioItem.vue';
import NxSaveStateBadge from '../NxSaveStateBadge.vue';
import { NxRadioItemBaseProps } from './radio-internal.js';

const props = defineProps<
  NxRadioItemBaseProps<T> & {
    swatchColor: string;
  }
>();
</script>

<template>
  <NxRadioItem v-bind="props">
    <template #default="{ checked, disabled, saveState, errorMessage, saveFadeOut }">
      <div
        class="nx-swatch-item"
        :class="{ checked, disabled }"
        :style="{ background: props.swatchColor }"
        :data-save-status="saveState"
      ></div>
      <!-- Sibling of the visual (anchored to .nx-radio) so the disabled fade
           of the swatch never dims the badge. -->
      <NxSaveStateBadge
        v-if="checked"
        class="save-badge"
        :state="saveState"
        :error-message="errorMessage"
        :fade-out="saveFadeOut"
      />
    </template>
  </NxRadioItem>
</template>

<style scoped>
.nx-swatch-item {
  width: calc(var(--nxt-size-unit) * 1.625);
  height: calc(var(--nxt-size-unit) * 1.625);
  border-radius: var(--nxt-radius-full);
  border: calc(var(--nxt-hairline) * 2) solid var(--nxt-surface-bg);
  box-shadow: 0 0 0 var(--nxt-hairline) var(--nxt-border);
  cursor: pointer;
  padding: 0;
  transition: box-shadow var(--nxt-motion-fast);
}

.nx-swatch-item:hover:not(.disabled) {
  box-shadow: 0 0 0 var(--nxt-hairline) var(--nxt-brand-bg-hover);
}

.nx-swatch-item.checked {
  box-shadow: 0 0 0 calc(var(--nxt-hairline) * 2) var(--nxt-brand-bg);
}

/* Disabled (incl. group save-loading): fade like the other inputs. The badge is
   a SIBLING of the swatch (anchored to .nx-radio), so it stays fully visible.
   The cursor is handled centrally in NxRadioItem (`.nx-radio.disabled`),
   because `:disabled`/`[disabled]` don't work on the <div> visual. */
.nx-swatch-item.disabled {
  /* opacity intentional: a disabled colour swatch must still show its
     (dimmed) colour — no bg token */
  opacity: 0.5;
}

/* --- I-422 save status: corner badge on the SELECTED swatch only (like an
   avatar status dot); the swatch's own ring stays untouched. The badge's
   surface-colored ring separates it from any swatch color. */
.save-badge {
  position: absolute;
  top: calc(-1 * var(--nxt-space-md) * 0.438);
  right: calc(-1 * var(--nxt-space-md) * 0.438);
}
</style>
